import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDesignStore } from '../store/designStore';
import { notifyParent } from './useDesignerInit';
import { safeGetItem } from '../lib/safeStorage';
import { HISTORY_SNAPSHOT_PROPS } from '../store/historyStore';

// Shared "finalize the design" action — the same POST /api/designs the header's
// Save button already uses, then either hands off to the embedding page
// (postMessage, iframe/popup launch) or redirects back to returnPath (bare
// launch). Originally CartPopup.tsx's own handleConfirm; extracted so both
// CartPopup and PreviewView's "Finish Design" button call the identical
// implementation instead of two copies drifting apart. See CartPopup.tsx's
// header comment for the full isPopupMode/returnPath branching rationale —
// unchanged here, just relocated.
export function useFinishDesign(isPopupMode: boolean) {
  const { t } = useTranslation();
  const {
    canvasInstances, sides, savedDesignId, setSavedDesignId, productConfig,
    nameNumbers, vdpData, sideColorCountJsonCache, resolveActiveColorCountKey,
    selectedVariantOptions, activeColorVariantId, hasSelectedColorVariant,
    selectedPrintMethod, selectedPrintColorOption,
    selectedPrintPositions, selectedPrintMethodByPosition, selectedPrintColorByPosition,
    clearDirtySideKeys,
  } = useDesignStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const finishDesign = async () => {
    if (!productConfig?.productId) {
      setError(t('designer.error.missingConfig'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const canvasMap: Record<string, Record<string, any>> = structuredClone(sideColorCountJsonCache);
      for (const side of sides) {
        const canvas = canvasInstances.get(side.sideKey);
        if (canvas) {
          const countKey = resolveActiveColorCountKey(side);
          canvasMap[side.sideKey] = {
            ...canvasMap[side.sideKey],
            [countKey]: (canvas as any).toJSON(HISTORY_SNAPSHOT_PROPS),
          };
        }
      }
      const canvasJson = btoa(JSON.stringify(canvasMap));
      const token = safeGetItem('session', 'customedge_token');

      const { buildDesignPreviewDataUrl } = await import('../lib/designPreviewCapture');
      const previewImageDataUrl = await buildDesignPreviewDataUrl().catch(() => null);

      const res = await fetch('/api/designs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          product_id:         productConfig.productId,
          sessionId:          safeGetItem('session', 'customedge_design_hash') ?? undefined,
          canvasJson,
          previewJson:        canvasJson,
          imageWidth:         sides[0]?.canvasWidth  ?? 400,
          imageHeight:        sides[0]?.canvasHeight ?? 400,
          design_id:          savedDesignId ?? undefined,
          customizePrices:    {},
          name_number:        nameNumbers.length ? JSON.stringify(nameNumbers) : undefined,
          vdp_data:           vdpData.length ? JSON.stringify(vdpData) : undefined,
          printingMethodData: (selectedPrintMethod || selectedPrintColorOption != null || selectedPrintPositions.length > 0)
            ? {
                method: selectedPrintMethod ?? undefined,
                printColorOption: selectedPrintColorOption ?? undefined,
                positions: selectedPrintPositions.length ? selectedPrintPositions : undefined,
                methodByPosition: Object.keys(selectedPrintMethodByPosition).length ? selectedPrintMethodByPosition : undefined,
                colorByPosition: Object.keys(selectedPrintColorByPosition).length ? selectedPrintColorByPosition : undefined,
              }
            : undefined,
          selectedVariantOptions: Object.keys(selectedVariantOptions).length ? selectedVariantOptions : undefined,
          colorVariantId: hasSelectedColorVariant ? (activeColorVariantId ?? undefined) : undefined,
          previewImageDataUrl: previewImageDataUrl ?? undefined,
          source: 'cart',
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.log ?? body.error ?? `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data.designId) {
        setSavedDesignId(data.designId);
        // This save sends the FULL current canvasMap for every side with
        // content (see the loop above) — see designStore.ts's
        // dirtySideKeys comment for why a successful save here means every
        // side is current, not just whichever one(s) were edited.
        clearDirtySideKeys();
        const widgetUrl = data.widgetUrl ? `${window.location.origin}${data.widgetUrl}` : '';

        if (isPopupMode) {
          notifyParent('DESIGNER_SAVED', { designId: data.designId, previewUrl: data.previewImageUrl ?? '', widgetUrl, price: 0 });
        } else {
          const returnPath = safeGetItem('session', 'customedge_magento_return_path');
          if (returnPath) {
            window.location.href = returnPath;
            return;
          }
        }
      }
      setDone(true);
    } catch (err: any) {
      setError(err.message ?? t('cart.error'));
    } finally {
      setLoading(false);
    }
  };

  return { finishDesign, loading, error, done, setError, setDone };
}
